// FP3-034E — Shared visuals for sessions that fall inside a provider
// unavailability window. The materializer (server.py / FP3-034E) tags
// each affected session with:
//   provider_unavailable: true
//   unavail_reason, unavail_reason_label, unavail_fidelity_treatment,
//   unavail_all_day
// We never DROP these sessions from the calendar — providers must still
// be able to see what would have been scheduled. Instead they render
// with a calm but unmistakable treatment + a clear chip.
//
// FP3-034E (polish pass) — bumped contrast so the at-a-glance read in
// Week and Month is obvious before reading any text. Excused = solid
// sage-grey chip with a diagonal "no-service" stripe pattern on the
// card. Make-up = solid amber chip with a thicker amber border + warm
// background so it pulls the eye toward action.
import { Bandaids, CalendarSlash, Coffee, GraduationCap, Notepad, WarningCircle } from "@phosphor-icons/react";

const REASON_ICON = {
  sick: Bandaids,
  personal: Coffee,
  pd: GraduationCap,
  meeting: Notepad,
  other: CalendarSlash,
};

// Treatment-based palette.
//
// Excused no-service = warm grey / sage. Calm, "no fault" feel.
//   chipBg is now solid sage so the pill reads against either a white
//   row (Today / Day) or the muted card bg (Week / Month).
// Needs make-up = warm amber. Action-required signal, never red.
//   chipBg is now solid amber so it pops in compressed Month chips.
//
// FP3-034F — Exported so any other compressed surface (e.g. Month
// chip) can read the same palette instead of inlining a near-copy.
// Each entry carries:
//   * chipBg / chipText / chipRing — the standard pill swatch
//   * shortLabel — used by Month / xxs sizes
//   * label — used by Day / Today / Week
//   * cardBg / cardBorder / cardAccent / cardOpacity — large-card vis
//   * stripePattern — diagonal "out-of-service" overlay
export const TREATMENT_META = {
  excused_no_service: {
    label: "Excused no-service",
    shortLabel: "Excused",
    // Solid chip — readable on any card bg.
    chipBg: "bg-[#7A736A]",
    chipText: "text-white",
    chipRing: "ring-[#7A736A]/40",
    // Solid hex equivalents — useful for inline `style` consumers
    // (e.g. month chips that swap the whole card background).
    chipBgHex: "#7A736A",
    chipTextHex: "#FFFFFF",
    cardBgHex: "#EFEDE6",
    cardBorderHex: "#B9B3A2",
    cardAccentHex: "#7A736A",
    cardTextHex: "#4A4540",
    // Subtle muted card on Week / Today rows.
    cardBg: "bg-[#EFEDE6]",
    cardBorder: "border-[#B9B3A2]",
    cardAccent: "#7A736A", // sage-grey accent bar
    cardOpacity: "opacity-90",
    // Diagonal "out-of-service" stripe used in Week + Month. Subtle
    // enough that text stays readable, distinctive enough that the
    // eye flags it before reading.
    stripePattern: "repeating-linear-gradient(135deg, transparent 0 6px, rgba(122,115,106,0.10) 6px 7px)",
  },
  make_up_needed: {
    label: "Needs make-up",
    shortLabel: "Make-up",
    // Solid amber chip.
    chipBg: "bg-[#C97B2E]",
    chipText: "text-white",
    chipRing: "ring-[#C97B2E]/40",
    chipBgHex: "#C97B2E",
    chipTextHex: "#FFFFFF",
    cardBgHex: "#FBEFD8",
    cardBorderHex: "#D9A05B",
    cardAccentHex: "#C97B2E",
    cardTextHex: "#5C3A14",
    // Warm amber card.
    cardBg: "bg-[#FBEFD8]",
    cardBorder: "border-[#D9A05B]",
    cardAccent: "#C97B2E", // amber accent bar
    cardOpacity: "opacity-100",
    // Faint amber-tinted stripe — pulls eye without alarming.
    stripePattern: "repeating-linear-gradient(135deg, transparent 0 6px, rgba(201,123,46,0.10) 6px 7px)",
  },
};

export function unavailMeta(session) {
  if (!session?.provider_unavailable) return null;
  const treatment = session.unavail_fidelity_treatment || "make_up_needed";
  const palette = TREATMENT_META[treatment] || TREATMENT_META.make_up_needed;
  const Icon = REASON_ICON[session.unavail_reason] || CalendarSlash;
  return {
    treatment,
    reason: session.unavail_reason || "other",
    reasonLabel: session.unavail_reason_label || "Unavailable",
    allDay: !!session.unavail_all_day,
    palette,
    Icon,
    chipLabel: palette.label,
    chipShort: palette.shortLabel,
  };
}

// Three sizes to match the surfaces:
//   sm  — Day view / Today row (full label + icon, ~24px tall)
//   xs  — Week view card (full label + icon, ~18px tall, denser)
//   xxs — Month view chip   (short label, ~14px tall, fits inside the
//                            ~28px-wide month chip without truncation)
export function UnavailableBadge({ session, size = "sm", testid, short = false }) {
  const meta = unavailMeta(session);
  if (!meta) return null;
  let sizing, iconSize, label;
  if (size === "xxs") {
    sizing = "text-[9px] px-1 py-0.5 gap-0.5 leading-none";
    iconSize = 9;
    label = meta.chipShort;
  } else if (size === "xs") {
    sizing = "text-[10px] px-1.5 py-0.5 gap-1 leading-none";
    iconSize = 10;
    label = short ? meta.chipShort : meta.chipLabel;
  } else {
    sizing = "text-[10px] px-2 py-0.5 gap-1 leading-tight";
    iconSize = 11;
    label = meta.chipLabel;
  }
  const { palette, Icon, chipLabel, reasonLabel, allDay } = meta;
  return (
    <span
      data-testid={testid || `unavailable-badge-${session.id || "x"}`}
      data-treatment={meta.treatment}
      data-reason={meta.reason}
      title={`Provider ${reasonLabel.toLowerCase()} — ${chipLabel}${allDay ? " (all day)" : ""}`}
      className={`inline-flex items-center font-semibold rounded-full ring-1 shadow-sm ${palette.chipBg} ${palette.chipText} ${palette.chipRing} ${sizing}`}
    >
      {meta.treatment === "make_up_needed" ? (
        <WarningCircle size={iconSize} weight="fill" />
      ) : (
        <Icon size={iconSize} weight="fill" />
      )}
      {label}
    </span>
  );
}

// Compact "Reason · time" caption rendered below the chip — feeds the
// "Sick · 9:00-11:00 AM" / "Meeting · all day" line.
export function UnavailableCaption({ session }) {
  const meta = unavailMeta(session);
  if (!meta) return null;
  return (
    <span
      data-testid={`unavailable-caption-${session.id || "x"}`}
      className="text-[10px] text-[#6E6859] italic"
    >
      {meta.reasonLabel} · {meta.allDay ? "all day" : "partial day"}
    </span>
  );
}
